import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClientEvent, MatrixClient, MatrixEvent, MatrixEventEvent } from "matrix-js-sdk";
import { EventStatus } from "matrix-js-sdk/lib/models/event-status";
import { Toast } from "src/components";
import { getCallParticipantLabel } from "src/scenes/Chat/components/call/ChatCallPanel.constants";
import {
    createMeetingNoteContent,
    MEETING_NOTE_EVENT_TYPE,
    MeetingCallNote,
    parseMeetingCallNote
} from "./meetingNotes";

export const useMeetingCallNotes = (
    matrixClient: MatrixClient,
    roomId: string,
    onNoteObserved?: (note: MeetingCallNote) => Promise<void> | void
) => {
    const [notesById, setNotesById] = useState<Record<string, MeetingCallNote>>({});
    const [isSending, setIsSending] = useState(false);
    const pendingSendCountRef = useRef(0);
    const observedNoteIdsRef = useRef(new Set<string>());

    useEffect(() => {
        if (!roomId) {
            return undefined;
        }

        const storeNote = (event: MatrixEvent): void => {
            if (event.getType() === "m.room.redaction") {
                const redactedEventId = event.getAssociatedId?.() || event.event.redacts || "";
                if (redactedEventId) {
                    setNotesById(previous => Object.fromEntries(
                        Object.entries(previous).filter(([, note]) =>
                            note.id !== redactedEventId && note.referenceEventId !== redactedEventId
                        )
                    ));
                }
                return;
            }
            const content = event.getContent<ReturnType<typeof createMeetingNoteContent> & {
                "m.new_content"?: ReturnType<typeof createMeetingNoteContent>;
                "m.relates_to"?: { rel_type?: string; event_id?: string };
            }>();
            const replacementEventId = content["m.relates_to"]?.rel_type === "m.replace"
                ? content["m.relates_to"]?.event_id
                : "";
            if (replacementEventId && content["m.new_content"]?.body) {
                setNotesById(previous => {
                    const noteEntry = Object.entries(previous)
                        .find(([, note]) => note.id === replacementEventId || note.referenceEventId === replacementEventId);
                    if (!noteEntry) {
                        return previous;
                    }
                    const [noteId, note] = noteEntry;
                    return {
                        ...previous,
                        [noteId]: {
                            ...note,
                            body: content["m.new_content"]?.body.trim() || note.body
                        }
                    };
                });
                return;
            }
            const note = parseMeetingCallNote(event, roomId);
            if (!note) {
                return;
            }
            const participantName = matrixClient.getRoom(roomId)?.getMember(note.authorUserId)?.name
                || matrixClient.getUser(note.authorUserId)?.displayName
                || "Guest";
            setNotesById(previous => ({
                ...previous,
                [note.id]: {
                    ...note,
                    authorName: getCallParticipantLabel(note.authorUserId, participantName)
                }
            }));
            const eventStatus = event.status;
            const isUnsentLocalEcho = [
                EventStatus.CANCELLED,
                EventStatus.ENCRYPTING,
                EventStatus.NOT_SENT,
                EventStatus.QUEUED,
                EventStatus.SENDING
            ].includes(eventStatus as EventStatus);
            if (!isUnsentLocalEcho && onNoteObserved && !observedNoteIdsRef.current.has(note.id)) {
                observedNoteIdsRef.current.add(note.id);
                Promise.resolve(onNoteObserved(note)).catch(() => {
                    observedNoteIdsRef.current.delete(note.id);
                });
            }
        };
        const storeDecryptedNote = (event: MatrixEvent, error?: Error): void => {
            if (!error) {
                storeNote(event);
            }
        };

        matrixClient.getRoom(roomId)
            ?.getUnfilteredTimelineSet()
            .getLiveTimeline()
            .getEvents()
            .forEach(storeNote);
        matrixClient.on(ClientEvent.Event, storeNote);
        matrixClient.on(MatrixEventEvent.Decrypted, storeDecryptedNote);
        return () => {
            matrixClient.off(ClientEvent.Event, storeNote);
            matrixClient.off(MatrixEventEvent.Decrypted, storeDecryptedNote);
        };
    }, [matrixClient, onNoteObserved, roomId]);

    const addNote = useCallback(async (body: string): Promise<boolean> => {
        const trimmedBody = body.trim();
        if (!trimmedBody || !roomId) {
            return false;
        }
        const createdAt = Date.now();
        const transactionId = window.crypto?.randomUUID?.()
            || `meeting-note-${createdAt}-${Math.random().toString(16).slice(2)}`;
        // const authorUserId = matrixClient.getUserId() || "";
        // const participantName = matrixClient.getRoom(roomId)?.getMember(authorUserId)?.name
        //     || matrixClient.getUser(authorUserId)?.displayName
        //     || "Guest";
        // const authorName = getCallParticipantLabel(authorUserId, participantName);
        pendingSendCountRef.current += 1;
        setIsSending(true);
        try {
            const content = createMeetingNoteContent(trimmedBody);
            await matrixClient.sendEvent(
                roomId,
                MEETING_NOTE_EVENT_TYPE,
                content,
                transactionId
            );

            return true;
        } catch (error) {
            console.warn("Unable to add the meeting note.", error);
            Toast.error("Unable to add the call note.");
            return false;
        } finally {
            pendingSendCountRef.current = Math.max(0, pendingSendCountRef.current - 1);
            setIsSending(pendingSendCountRef.current > 0);
        }
    }, [matrixClient, roomId]);

    const editNote = useCallback(async (note: MeetingCallNote, body: string): Promise<boolean> => {
        const trimmedBody = body.trim();
        if (!trimmedBody || !roomId || note.authorUserId !== matrixClient.getUserId()) {
            return false;
        }
        const previousBody = note.body;
        setNotesById(previous => ({
            ...previous,
            [note.id]: { ...note, body: trimmedBody }
        }));
        try {
            await matrixClient.sendEvent(roomId, MEETING_NOTE_EVENT_TYPE, {
                msgtype: "m.text",
                body: `* ${trimmedBody}`,
                "m.new_content": createMeetingNoteContent(trimmedBody),
                "m.relates_to": { rel_type: "m.replace", event_id: note.referenceEventId || note.id }
            });
            return true;
        } catch (error) {
            setNotesById(previous => ({
                ...previous,
                [note.id]: { ...note, body: previousBody }
            }));
            console.warn("Unable to edit the meeting note.", error);
            Toast.error("Unable to edit the call note.");
            return false;
        }
    }, [matrixClient, roomId]);

    const deleteNote = useCallback(async (note: MeetingCallNote): Promise<boolean> => {
        if (!roomId || note.authorUserId !== matrixClient.getUserId()) {
            return false;
        }
        setNotesById(previous => {
            const next = { ...previous };
            delete next[note.id];
            return next;
        });
        try {
            await matrixClient.redactEvent(roomId, note.referenceEventId || note.id);
            return true;
        } catch (error) {
            setNotesById(previous => ({ ...previous, [note.id]: note }));
            console.warn("Unable to delete the meeting note.", error);
            Toast.error("Unable to delete the call note.");
            return false;
        }
    }, [matrixClient, roomId]);

    const notes = useMemo(() => Object.values(notesById)
        .sort((firstNote, secondNote) => firstNote.createdAt - secondNote.createdAt), [notesById]);

    return { addNote, deleteNote, editNote, isSending, notes };
};
